import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../hooks/useResponsive";
import { colors } from "../styles/colors";
import { PrimaryButton, SecondaryButton } from './ui/StandardButton';
import { buildLocalizedPath } from "../utils/routes";
import LazyVideo from "./LazyVideo";
import { useContent } from "../cms/ContentContext";

const Experiences = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();

  // Memoize experience links based on language
  const experienceLinks = useMemo(
    () => [
      buildLocalizedPath(i18n.language, "snowmobileSafari"),
      buildLocalizedPath(i18n.language, "northernLights"),
      buildLocalizedPath(i18n.language, "huskyTour"),
      buildLocalizedPath(i18n.language, "accommodation"),
    ],
    [i18n.language]
  );

  const experiences = [
    {
      title: t("experiences.snowmobileTitle"),
      description: t("experiences.snowmobileDesc"),
      videoSrc: getMedia('experiences.snowmobile.videoSrc', '/optimized_videos/snoskoter2-opt.mp4'),
      poster: getMedia('experiences.snowmobile.poster', '/images/Nya_bilder/IMG_6698.webp'),
      features: [
        t("experiences.snowmobileFeature1"),
        t("experiences.snowmobileFeature2"),
        t("experiences.snowmobileFeature3"),
        t("experiences.snowmobileFeature4"),
      ],
      learnMoreKey: "experiences.learnMoreSnowmobile",
    },
    {
      title: t("experiences.northernLightsTitle"),
      description: t("experiences.northernLightsDesc"),
      videoSrc: getMedia('experiences.northernLights.videoSrc', '/optimized_videos/norrsken1-opt.mp4'),
      poster: getMedia('experiences.northernLights.poster', '/images/Nya_bilder/norrsken1.webp'),
      features: [
        t("experiences.northernLightsFeature1"),
        t("experiences.northernLightsFeature2"),
        t("experiences.northernLightsFeature3"),
        t("experiences.northernLightsFeature4"),
      ],
      learnMoreKey: "experiences.learnMoreNorthernLights",
    },
    {
      title: t("experiences.dogSleddingTitle"),
      description: t("experiences.dogSleddingDesc"),
      videoSrc: getMedia('experiences.dogSledding.videoSrc', '/optimized_videos/hundspann2-opt.mp4'),
      poster: getMedia('experiences.dogSledding.poster', '/images/Nya_bilder/hundspann1.webp'),
      features: [
        t("experiences.dogSleddingFeature1"),
        t("experiences.dogSleddingFeature2"),
        t("experiences.dogSleddingFeature3"),
        t("experiences.dogSleddingFeature4"),
      ],
      learnMoreKey: "experiences.learnMoreDogSledding",
    },
    {
      title: t("experiences.lodgingTitle"),
      description: t("experiences.lodgingDesc"),
      videoSrc: getMedia('experiences.lodging.videoSrc', '/skolan-bilder/Skolan-film-2.mp4'),
      poster: getMedia('experiences.lodging.poster', '/skolan-bilder/skolan1-4fe44306.jpg'),
      alt: t("experiences.lodgingAlt"),
      features: [
        t("experiences.lodgingFeature1"),
        t("experiences.lodgingFeature2"),
        t("experiences.lodgingFeature3"),
        t("experiences.lodgingFeature4"),
      ],
      learnMoreKey: "experiences.learnMoreLodging",
    },
  ];

  return (
    <section
      data-cms-section="experiences" data-cms-page="home"
      className="py-20"
      style={{ background: colors.gradients.heroSolid }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`font-bold mb-6 ${isMobile ? 'text-3xl' : 'text-4xl md:text-5xl'
              }`}
            style={{ color: colors.text.primary }}
          >
            {t('experiences.title')}
          </h2>
          <p
            className={`max-w-3xl mx-auto ${isMobile ? 'text-lg' : 'text-xl'
              }`}
            style={{ color: colors.text.secondary }}
          >
            {t('experiences.intro')}
          </p>
        </div>

        <div className={isMobile ? 'space-y-12' : 'space-y-20'}>
          {experiences.map((experience, index) => (
            <div
              key={index}
              className={`flex flex-col gap-12 ${!isMobile && index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                } ${!isMobile ? 'lg:flex-row lg:items-center' : ''}`}
            >
              {/* Image */}
              <div className={isMobile ? 'w-full' : 'lg:w-1/2'}>
                <div className="relative group overflow-hidden rounded-2xl shadow-2xl border border-white/10">
                  {experience.videoSrc ? (
                    <LazyVideo
                      src={experience.videoSrc}
                      poster={experience.poster}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full"
                      videoClassName={`w-full object-cover object-center transition-transform duration-700 origin-center scale-125 group-hover:scale-150 ${isMobile ? 'h-64' : 'h-96'
                        }`}
                      pauseWhenOutOfView
                    />
                  ) : (
                    <img
                      src={experience.image}
                      alt={experience.alt || experience.title}
                      className={`w-full object-cover object-center transition-transform duration-700 origin-center scale-125 group-hover:scale-150 ${isMobile ? 'h-64' : 'h-96'
                        }`}
                    />
                  )}
                  {/* Mycket subtil frost-effekt som inte blockerar sikten */}
                  <div className="absolute inset-0 bg-white/5 backdrop-blur-[0.5px]"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>

              {/* Content */}
              <div className={isMobile ? 'w-full' : 'lg:w-1/2'}>
                <h3 className={`font-bold text-white mb-6 ${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'
                  }`}>
                  {experience.title}
                </h3>

                <p className={`text-gray-300 mb-8 leading-relaxed ${isMobile ? 'text-base' : 'text-lg'
                  }`}>
                  {experience.description}
                </p>

                <div className={`gap-4 mb-8 ${isMobile ? 'grid grid-cols-1' : 'grid grid-cols-2'
                  }`}>
                  {experience.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full flex-shrink-0"></div>
                      <span className={`text-gray-300 ${isMobile ? 'text-sm' : ''
                        }`}>{feature}</span>
                    </div>
                  ))}
                </div>

                <SecondaryButton
                  to={experienceLinks[index]}
                  className={isMobile ? 'w-full' : ''}
                >
                  {t(experience.learnMoreKey)}
                </SecondaryButton>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <div className="relative bg-white/5 backdrop-blur-[0.5px] rounded-2xl p-8 border border-white/10 overflow-hidden">
            {/* Extra subtil frost effect för CTA-kortet */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/2 to-transparent"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-white mb-4">
                {t('experiences.ctaTitle')}
              </h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                {t('experiences.ctaDesc')}
              </p>
              <PrimaryButton
                to={buildLocalizedPath(i18n.language, "book")}
                size="large"
              >
                {t('experiences.ctaBook')}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experiences;
